import React, { useState } from "react";
import { Plus, Check, Pencil, Trash2 } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { daysLeft, statusClass, todayISO, toast } from "../../lib/utils";

const PRIO = ["Critical", "High", "Medium", "Low"];
const prioRank = { Critical: 0, High: 1, Medium: 2, Low: 3 };
const prioTone = (p) => p === "Critical" ? "red" : p === "High" ? "amber" : p === "Medium" ? "blue" : "neutral";

export default function Actions() {
  const { doc, mutate, readOnly } = useProject();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const sorted = [...doc.actions].sort((a, b) => (prioRank[a.priority] ?? 3) - (prioRank[b.priority] ?? 3));

  const markDone = (id) => mutate(d => ({ ...d, actions: d.actions.map(a => a.id === id ? { ...a, status: "Completed" } : a) }));

  const remove = (id) => {
    if (!window.confirm("Delete this action?")) return;
    mutate(d => ({ ...d, actions: d.actions.filter(a => a.id !== id) }));
    setEditing(null);
  };

  const saveEdit = () => {
    if (!editing) return;
    const g = (k) => document.getElementById("ea-" + k)?.value;
    mutate(d => ({
      ...d,
      actions: d.actions.map(a => a.id === editing.id ? {
        ...a,
        title:    g("title")  ?? a.title,
        assignee: g("assign") ?? a.assignee,
        owner:    g("own")    ?? a.owner,
        priority: g("prio")   ?? a.priority,
        status:   g("stat")   ?? a.status,
        due:      g("due")    ?? a.due,
        notes:    g("notes")  ?? a.notes,
      } : a),
    }));
    setEditing(null);
    toast("Saved");
  };

  const addAction = () => {
    const g = (k) => document.getElementById("na-" + k)?.value;
    const title = g("title");
    if (!title) return toast("Title required");
    const n = doc.actions.length + 1;
    mutate(d => ({
      ...d, actions: [...d.actions, {
        id: `AI-${String(n).padStart(3, "0")}`, title,
        owner: g("own") || "TBD",
        assignee: g("assign") || "TBD",
        priority: g("prio"),
        status: "Not Started",
        due: g("due"),
        created: todayISO(),
        tags: [],
        notes: g("notes") || "",
      }],
    }));
    setShowAdd(false);
  };

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Workstream</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Action Items</h2>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="btn-add-action" disabled={readOnly} style={readOnly ? { display: "none" } : {}}><Plus size={14}/> Add Action</button>
      </div>

      <AIPanel title="Weekly Digest" section="actions" buildPrompt={() => prompts.actionDigest(doc)} buttonLabel="Generate Digest"/>

      <div className="card overflow-x-auto">
        <table className="tbl">
          <thead><tr>
            <th>ID</th><th>Title</th><th>Assignee</th><th>Priority</th><th>Status</th><th>Due</th><th>Days</th><th></th>
          </tr></thead>
          <tbody>
            {sorted.map(a => {
              const days = daysLeft(a.due);
              const over = a.status === "Overdue" || (days < 0 && a.status !== "Completed");
              return (
                <tr key={a.id} style={{ background: over ? "rgba(239,68,68,0.04)" : "" }}>
                  <td className="font-mono text-xs text-zinc-500">{a.id}</td>
                  <td className="max-w-[300px]">
                    <div className="text-sm font-medium text-zinc-900">{a.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">{a.notes}</div>
                  </td>
                  <td className="font-mono text-xs text-zinc-500">{a.assignee}</td>
                  <td><Pill tone={prioTone(a.priority)}>{a.priority}</Pill></td>
                  <td><Pill tone={statusClass(a.status).replace("pill-","")}>{a.status}</Pill></td>
                  <td className="font-mono text-xs text-zinc-500">{a.due}</td>
                  <td className="font-mono text-xs"
                      style={{ color: a.status === "Completed" ? "#22C55E" : over ? "#EF4444" : days <= 7 ? "#F59E0B" : "#A1A1AA" }}>
                    {a.status === "Completed" ? "✓ Done" : over ? `${Math.abs(days)}d late` : `${days}d`}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {!readOnly && a.status !== "Completed" && (
                        <button className="btn btn-ghost btn-sm" onClick={() => markDone(a.id)} title="Mark done" data-testid={`btn-mark-done-${a.id}`}><Check size={12}/></button>
                      )}
                      {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(a)} title="Edit" data-testid={`btn-edit-action-${a.id}`}><Pencil size={12}/></button>}
                      {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => remove(a.id)} title="Delete" data-testid={`btn-del-action-${a.id}`}><Trash2 size={12}/></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Action Item"
             footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                       <button className="btn btn-primary" onClick={addAction} data-testid="btn-save-action">Add Action</button></>}>
        <Field label="Title"><input id="na-title" className="fld" placeholder="Action item description" data-testid="input-action-title"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assignee"><input id="na-assign" className="fld"/></Field>
          <Field label="Owner"><input id="na-own" className="fld"/></Field>
          <Field label="Priority">
            <select id="na-prio" className="fld" defaultValue="High">{PRIO.map(p=><option key={p}>{p}</option>)}</select>
          </Field>
          <Field label="Due Date"><input id="na-due" type="date" className="fld" defaultValue={todayISO()}/></Field>
        </div>
        <Field label="Notes"><textarea id="na-notes" className="fld" rows={3}/></Field>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Edit ${editing.id}` : ""}
             footer={<><button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                       <button className="btn btn-primary" onClick={saveEdit} data-testid="btn-save-edit-action">Save</button></>}>
        {editing && (
          <>
            <Field label="Title"><input id="ea-title" className="fld" defaultValue={editing.title}/></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Assignee"><input id="ea-assign" className="fld" defaultValue={editing.assignee}/></Field>
              <Field label="Owner"><input id="ea-own" className="fld" defaultValue={editing.owner}/></Field>
              <Field label="Priority">
                <select id="ea-prio" className="fld" defaultValue={editing.priority}>{PRIO.map(p=><option key={p}>{p}</option>)}</select>
              </Field>
              <Field label="Status">
                <select id="ea-stat" className="fld" defaultValue={editing.status}>{["Not Started","In Progress","Completed","Overdue","Blocked"].map(o=><option key={o}>{o}</option>)}</select>
              </Field>
              <Field label="Due Date" span={2}><input id="ea-due" type="date" className="fld" defaultValue={editing.due}/></Field>
            </div>
            <Field label="Notes"><textarea id="ea-notes" className="fld" rows={3} defaultValue={editing.notes}/></Field>
          </>
        )}
      </Modal>
    </>
  );
}
